import { passwordPolicy } from '@facture/contracts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { LockIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { AuthShell } from '@/components/app/auth-shell'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { ApiRequestError, api } from '@/lib/api'

export const Route = createFileRoute('/reset-password/$token')({ component: ResetPasswordPage })

const schema = z
  .object({ password: passwordPolicy, confirm: z.string() })
  .refine((v) => v.password === v.confirm, { path: ['confirm'], message: 'Las contraseñas no coinciden' })
type Values = z.infer<typeof schema>

function ResetPasswordPage() {
  const { token } = Route.useParams()
  const navigate = useNavigate()
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirm: '' },
  })
  const reset = useMutation({
    mutationFn: (values: Values) =>
      api.post('/auth/reset-password', { token, password: values.password }, { global: true }),
    onSuccess: () => {
      toast.success('Contraseña actualizada. Ya podés iniciar sesión')
      navigate({ to: '/login', replace: true })
    },
    onError: (error) =>
      form.setError('root', {
        message: error instanceof ApiRequestError ? error.message : 'No se pudo cambiar la contraseña',
      }),
  })
  const rootError = form.formState.errors.root?.message

  return (
    <AuthShell>
      <h1 className="font-semibold text-[28px] tracking-tight">Elegí una contraseña nueva</h1>
      <p className="mt-2 text-muted-foreground text-sm">
        Mínimo 10 caracteres, con letras y números. Al cambiarla se cierran tus otras sesiones.
      </p>
      <form onSubmit={form.handleSubmit((v) => reset.mutate(v))} className="mt-8 grid gap-5" noValidate>
        <Field label="Contraseña nueva" error={form.formState.errors.password}>
          {(control) => (
            <Input
              {...control}
              {...form.register('password')}
              type="password"
              autoComplete="new-password"
              autoFocus
              leading={<LockIcon />}
              className="[&_input]:h-11"
            />
          )}
        </Field>
        <Field label="Repetila" error={form.formState.errors.confirm}>
          {(control) => (
            <Input
              {...control}
              {...form.register('confirm')}
              type="password"
              autoComplete="new-password"
              leading={<LockIcon />}
              className="[&_input]:h-11"
            />
          )}
        </Field>
        {rootError && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/25 bg-destructive-soft px-3 py-2.5 text-destructive-soft-foreground text-sm"
          >
            {rootError}{' '}
            <Link to="/forgot-password" className="font-medium underline">
              Pedir otro enlace
            </Link>
          </div>
        )}
        <Button type="submit" size="lg" loading={reset.isPending} className="w-full">
          Cambiar contraseña
        </Button>
      </form>
    </AuthShell>
  )
}
